import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { EndPoints } from "../../Constants/endpoints";
import { useToast } from "../../Services/ToastContext";
import useAddPrescriptionToPatient from "../../api/Prescription/useAddPrescriptionToPatient";
import { handleExportPDF } from "./utils";
import { cleanFormData } from "./hooks/usePrescriptionSubmit";
import { useGetData, usePostData } from "../../hooks";
import { PrescriptionBaseForm } from "./components";
import { PrescriptionDType } from "../../interface/GeneralResponse";

const AddPrescriptionToPatient: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isItComeFromMyPrescription = location.state?.isItComeFromMyPrescription;
  const { PatientId, PrescriptionId } = useParams();
  const [addedPrescriptionId, setAddedPrescriptionId] = useState<number>();
  const [localInitialData, setLocalInitialData] = useState<any>(undefined);

  const { data: prescriptionData } = useGetData<PrescriptionDType>(
    EndPoints.GetPrescriptions,
    {},
    PrescriptionId,
  );

  const prescription = prescriptionData?.Data?.Prescription;

  useEffect(() => {
    if (prescription) {
      const drugsData =
        prescription.Drugs?.map((drug: any) => {
          if (!drug.PackageId && drug.Name) {
            return {
              Name: drug.Name,
              DrugName: drug.DrugName,
              Frequency: drug.FrequencyCode || "",
              SelectedDays: drug.Days
                ? drug.Days.map((day: any) => day.Id)
                : [],
              Meal: drug.MealCode || "",
              Sleep: drug.Sleep || false,
              Times: drug.Times,
              Duration: drug.Duration,
              Instructions: drug.Instructions || "",
              DosageNum: drug.DosageNum,
              DosageUnit: drug.DosageUnit,
            };
          } else {
            return {
              PackageId: drug.PackageId,
              DrugName: drug.DrugName,
              Frequency: drug.FrequencyCode || "",
              SelectedDays: drug.Days
                ? drug.Days.map((day: any) => day.Id)
                : [],
              Meal: drug.MealCode || "",
              Sleep: drug.Sleep || false,
              Times: drug.Times,
              Duration: drug.Duration,
              Instructions: drug.Instructions || "",
              DosageNum: drug.DosageNum,
              DosageUnit: drug.DosageUnit,
            };
          }
        }) || [];

      setLocalInitialData({
        Case: prescription.Case || "",
        Drugs: drugsData,
      });
    }
  }, [prescription]);

  const handleBack = () => {
    navigate(
      isItComeFromMyPrescription
        ? "/my_prescriptions"
        : location.state?.from || -1,
    );
  };

  const { mutate, isPending } = useAddPrescriptionToPatient();
  const { mutate: printMutate } = usePostData(
    `${EndPoints.PrintPrescription}/${addedPrescriptionId}`,
  );

  useEffect(() => {
    if (addedPrescriptionId) {
      printMutate(
        {},
        {
          onSuccess: (data) => {
            handleExportPDF(data);
            setAddedPrescriptionId(undefined);
            if (PrescriptionId) {
              // Navigate to my_prescriptions when editing
              navigate("/my_prescriptions");
            } else {
              // Navigate back when adding new prescription
              handleBack();
            }
          },
        },
      );
    }
  }, [addedPrescriptionId, printMutate, navigate]);

  const handleSubmit = (data: any) => {
    const cleanedData = cleanFormData(data);
    mutate(
      { patientId: Number(PatientId), data: cleanedData },
      {
        onSuccess: (response) => {
          showToast(t("prescription_added_successfully"), "success");
          setAddedPrescriptionId(response?.Data?.Id);
          queryClient.refetchQueries({
            queryKey: [EndPoints.GetPrescriptions],
          });
          queryClient.refetchQueries({
            queryKey: [EndPoints.GetPrescriptionToPatient + "/" + PatientId],
          });
        },

        onError: (error: any) => {
          showToast(error.message || t("error_adding_prescription"), "error");
        },
      },
    );
  };

  return (
    <PrescriptionBaseForm
      patientId={PatientId}
      onSubmit={handleSubmit}
      initialData={localInitialData}
      config={{
        title: t("add_prescription"),
        submitButtonText: t("save_and_print"),
        caseLabel: t("case_name"),
        showPatientInfo: true,
        showPrint: true,
      }}
      isLoading={isPending}
    />
  );
};

export default AddPrescriptionToPatient;
